import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { UserGroupService } from 'src/app/modules/settings/services/user-group-service';
import { EmployeeUserGroupViewModel } from 'src/app/modules/settings/viewModels/userGroups/employeeUserGroupViewModel';
import { UserGroupViewModel } from 'src/app/modules/settings/viewModels/userGroups/userGroupViewModel';
import { BsModalConfig } from 'src/app/modules/shared/config/modal.config';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { T } from 'src/assets/i18n/translation-keys';
import { AddGroupsToEmployeeComponent } from '../../modals/user-groups/add-groups-to-employee-modal/add-groups-to-employee-modal.component';

@Component({
    selector: 'app-employee-user-groups-section',
    templateUrl: 'employee-user-groups-section.html',
    styleUrls: ['employee-user-groups-section.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EmployeeUserGroupsSectionComponent implements OnInit {
  private subscriptions: Subscription = new Subscription();
  @Input() employee: EmployeeDetailsViewModel;
  @Input() canEdit: boolean = true;
  @Output() onGroupsChanged = new EventEmitter<UserGroupViewModel[]>();
  groups: UserGroupViewModel[] = [];
  allGroups: UserGroupViewModel[] = [];
  public localisedUserGroups = 'User Groups';
  public readonly T = T;
  private modalRef: BsModalRef;

  constructor(
    private readonly userGroupService: UserGroupService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly modalService: BsModalService,
    private readonly modalConfig: BsModalConfig,
    private readonly alertService: AlertService,
    private readonly localisationService: LocalisationService,
    private readonly translateService: TranslateService
  ) {}
  ngOnInit(): void {
    this.localisedUserGroups = this.localisationService.localiseObjectType(ObjectTypes.User_Group, true);
    if (this.employee && this.employee.id) {
      this.subscriptions.add(
        this.userGroupService.listByEmployeeId(this.employee.id).subscribe((res) => {
          this.groups = res;
          this.changeDetectorRef.markForCheck();
        })
      );
    } else {
      this.subscriptions.add(
        this.userGroupService.list().subscribe((res) => {
          this.allGroups = res;
          this.changeDetectorRef.markForCheck();
        })
      );
    }
  }

  openManageGroupsModal() {
    const copiedSelectedGroups = JSON.parse(JSON.stringify(this.groups)) as UserGroupViewModel[];
    const initialState = {
      employee: this.employee,
      selectedGroups: copiedSelectedGroups,
    };

    const modalParams = Object.assign({}, this.modalConfig, { initialState });
    this.modalRef = this.modalService.show(AddGroupsToEmployeeComponent, modalParams);
    this.modalRef.content.onSave.subscribe((groups: UserGroupViewModel[]) => {
      if (this.employee && this.employee.id) {
        const newGroups = groups.filter(group => !this.groups.some(g => g.id === group.id));
        const groupsToAdd = newGroups.length < this.groups.length ? newGroups : groups;
        const employeeIdsToAdd = this.getGroupIdsToAdd(groupsToAdd);
        const employeeUserGroupsToDelete = this.getEmployeeIdsToDelete(groups);

        if (employeeIdsToAdd && employeeIdsToAdd.length) {
          this.userGroupService.addUserToGroups(this.employee.id, employeeIdsToAdd).subscribe((employeeUserGroups) => {
            this.groups = groups;
            this.groups = this.groups.slice();
            this.changeDetectorRef.markForCheck();
            void this.alertService.success(this.translateService.instant(T.common.item_was_added_successfully, { item: this.localisedUserGroups }));
          });
        }

        if (employeeUserGroupsToDelete && employeeUserGroupsToDelete.length) {
          this.userGroupService.deleteEmployeeUserGroups(employeeUserGroupsToDelete).subscribe((res) => {
            this.groups = groups;
            this.groups = this.groups.slice();
            this.changeDetectorRef.markForCheck();
            void this.alertService.success(this.translateService.instant(T.common.item_removed, { item: this.localisedUserGroups }));
          });
        }
      } else {
        this.updateTempGroups(groups);
        this.onGroupsChanged.next(this.groups);
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  getGroupIdsToAdd(selectedGroups: UserGroupViewModel[]): number[] {
    const groupIds = this.allGroups.map(group => group.id);
    const selectedGroupIds = selectedGroups.map(group => group.id);

    return selectedGroupIds.filter(id => !groupIds.includes(id));
  }

  getEmployeeIdsToDelete(selectedGroups: UserGroupViewModel[]): EmployeeUserGroupViewModel[] {
    const groupIds = [];
    const groupsToDelete: EmployeeUserGroupViewModel[] = [];
    selectedGroups.forEach((g) => groupIds.push(g.id));

    this.groups.forEach((group) => {
      if (groupIds.indexOf(group.id) < 0) {
        const matchingEmployeeUserGroup = group.employeeUserGroups.find((g) => g.employeeId === this.employee.id);

        if (matchingEmployeeUserGroup) {
          groupsToDelete.push(matchingEmployeeUserGroup);
        }
      }
    });

    return groupsToDelete;
  }

  addGroups(selectedGroups: UserGroupViewModel[]): number[] {
    const newIds = [];
    const groupIds = [];
    this.groups.forEach((g) => groupIds.push(g.id));

    selectedGroups.forEach((e) => {
      if (groupIds.indexOf(e.id) < 0) {
        newIds.push(e.id);
      }
    });

    return newIds;
  }

  updateTempGroups(selectedGroups: UserGroupViewModel[]): void {
    const selectedGroupIds = [];
    const groupIds = [];
    this.groups.forEach((g) => groupIds.push(g.id));

    selectedGroups.forEach((e) => {
      if (groupIds.indexOf(e.id) < 0) {
        this.groups.push(e);
      }
      selectedGroupIds.push(e.id);
    });

    this.groups = this.groups.filter((g) => selectedGroupIds.indexOf(g.id) >= 0);
  }
}
